import { db } from './db.js';
import { t } from './i18n.js';
import { ARCHIVE_FILMS } from './archive-films.js';
import { withoutBlocked } from './content-block.js';

/* «Cine clásico»: películas de dominio público de Internet Archive (archive.org), elegidas a mano (ver
   scripts/archive-curate.js). No hace falta cuenta ni lista: es un proveedor gratuito más que se añade con un toque en
   «Canales gratuitos». El catálogo está en el propio código; el vídeo se reproduce directamente desde archive.org y la
   carátula y la sinopsis las pone TMDB si hay (si no, la miniatura de Internet Archive).

   Es el contenido sobre el que tiene sentido poner anuncios (ver core/ads.js): son obras con derechos libres. */

export const ARCHIVE_PROFILE_TYPE = 'archive';

const GENRES = ['horror', 'scifi', 'silent', 'comedy', 'classic'];
const GENRE_ICON = { horror: 'Terror', scifi: 'Ciencia ficción', silent: 'Cine mudo', comedy: 'Comedia', classic: 'Clásicos' };

const fileUrl = (film) => 'https://archive.org/download/' + encodeURIComponent(film.id) + '/' + encodeURIComponent(film.file);
const thumbUrl = (film) => 'https://archive.org/services/img/' + encodeURIComponent(film.id);

/** Título de un género en el idioma de la interfaz (con el nombre en español como respaldo). */
function genreName(g) {
    const key = 'archive.genre.' + g;
    const v = t(key);
    return v === key ? GENRE_ICON[g] || g : v;
}

/** Catálogo con la misma forma que el resto de proveedores: { live, vod, series, categories }. Módulo puro. */
export function buildArchiveCatalog(films = ARCHIVE_FILMS) {
    const used = GENRES.filter((g) => films.some((f) => f.genre === g));
    const vod = films.map((f) => ({
        id: 'ia-' + f.id,
        name: f.title,
        poster: thumbUrl(f),
        categoryId: 'ia-' + f.genre,
        ext: 'mp4',
        added: 0,
        rating: 0,
        year: String(f.year || ''),
        plot: '',
        genre: genreName(f.genre),
        duration: '',
        durationSecs: f.secs || 0,
        tmdb: '',
        url: fileUrl(f),
        size: f.size || 0,
        language: 'en'
    }));
    return {
        vod,
        series: [],
        live: [],
        categories: { live: [], vod: used.map((g) => ({ id: 'ia-' + g, name: genreName(g) })), series: [] }
    };
}

export class ArchiveClient {
    /** lookup(kind, item) consulta TMDB (lo inyecta clients.js); sin él se queda lo que hay. */
    constructor(profile, { lookup = null } = {}) {
        this.profile = profile;
        this.lookup = lookup;
    }

    _account() {
        return {
            user_info: { username: 'archive', auth: 1, status: 'Active', exp_date: null, max_connections: 0, active_cons: 0 },
            server_info: { url: 'archive.org', product: 'Internet Archive' }
        };
    }

    async auth() {
        return this._account();
    }

    async fetchCatalog() {
        const account = this._account();
        const data = withoutBlocked(buildArchiveCatalog(), { isArchive: true });
        await this.decorate(data);
        return Object.assign({ updatedAt: Date.now(), account: { user: account.user_info, server: account.server_info } }, data);
    }

    /** Carátula, sinopsis y nota de TMDB; lo ya consultado se guarda en el dispositivo. */
    async decorate(data, { budgetMs = 15000 } = {}) {
        const key = 'archive-meta:' + this.profile.id;
        const known = (await db.get('meta', key).catch(() => null)) || {};
        const todo = [];
        data.vod.forEach((item) => {
            const hit = known[item.id];
            if (hit) Object.assign(item, hit);
            else if (this.lookup) todo.push(item);
        });
        let stop = false;
        const deadline = Date.now() + budgetMs;
        const worker = async () => {
            while (todo.length && !stop && Date.now() < deadline) {
                const item = todo.shift();
                let x;
                try {
                    x = await this.lookup('vod', item);
                } catch (e) {
                    stop = true;
                    return;
                }
                const meta = x && x.tmdbId ? { tmdb: String(x.tmdbId), poster: x.poster || item.poster, backdrop: x.backdrop || '', plot: x.overview || '', rating: x.rating || 0 } : { tmdb: '' };
                known[item.id] = meta;
                Object.assign(item, meta);
            }
        };
        if (todo.length) {
            await Promise.all([worker(), worker(), worker(), worker()]);
            await db.put('meta', key, known).catch(() => {});
        }
        return data;
    }

    async info(kind, id, base) {
        return Object.assign({}, base);
    }

    vodUrl(id, ext, item) { return item && item.url ? item.url : ''; }
    seriesUrl() { return ''; }
    liveUrl() { return ''; }
    xmltvUrl() { return ''; }
}
